import { inject } from '@angular/core';
import { combineLatest, map, Observable, switchMap, take } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { GamesService, LineupsService } from '@games/infra';
import { GameLineupsDto, GameLineupsDtoLineup, GamePlayerDto, GetGameByIdResponse, LineupPlayer } from '@core-http';
import { resolvePlayerThumbnail } from '@core';
import { LineupsPlayerModel, LineupsQueryResult } from './lineups.query-result';

export class LineupsQueryHandler {
  private readonly gamesService: GamesService = inject(GamesService);
  private readonly lineupsService: LineupsService = inject(LineupsService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  getLineups(): Observable<LineupsQueryResult> {
    return this.activatedRoute.params.pipe(
      take(1),
      switchMap((params: Params) => {
        const gameId: string = params['id'];

        return combineLatest([
          this.gamesService.getOneGame({ gameId }),
          this.lineupsService.getLineups({ gameId })
        ]).pipe(
          take(1),
          map(([game, lineups]: [GetGameByIdResponse, GameLineupsDto]) => this.mapToQueryResult(game, lineups))
        );
      })
    );
  }

  private mapToQueryResult(game: GetGameByIdResponse, lineups: GameLineupsDto): LineupsQueryResult {
    if (lineups?.lineups?.length) {
      return this.loadExistingLineups(game, lineups);
    }
    return this.loadGamePlayers(game);
  }

  private loadExistingLineups(game: GetGameByIdResponse, lineups: GameLineupsDto): LineupsQueryResult {
    const lineupPlayers: LineupPlayer[] = lineups.lineups.flatMap(
      (lineup: GameLineupsDtoLineup) => lineup.lineupPlayers
    );

    const findGamePlayer = (playerId: string): GamePlayerDto | undefined => {
      return game.gamePlayers?.find((gamePlayer: GamePlayerDto) => gamePlayer.playerId === playerId);
    };

    const players: LineupsPlayerModel[] = lineupPlayers.map((lineupPlayer: LineupPlayer) => {
      const gamePlayer: GamePlayerDto | undefined = findGamePlayer(lineupPlayer.playerId);

      return {
        playerId: lineupPlayer.playerId,
        userId: lineupPlayer.userId,
        userDisplayName: lineupPlayer.userDisplayName,
        userThumbnail: resolvePlayerThumbnail(lineupPlayer.userId, lineupPlayer.userThumbnail),
        preferredPosition: gamePlayer?.playerPreferences?.preferredPosition ?? null,
        preferredLevel: gamePlayer?.playerPreferences?.preferredLevel ?? null
      };
    });

    return {
      isLineupsSaved: true,
      isHost: game.isCurrentUserHost,
      players
    };
  }

  private loadGamePlayers(game: GetGameByIdResponse): LineupsQueryResult {
    const players: LineupsPlayerModel[] =
      game.gamePlayers?.map((player: GamePlayerDto) => ({
        playerId: player.playerId,
        userId: player.userId,
        userDisplayName: player.userDisplayName,
        userThumbnail: resolvePlayerThumbnail(player.userId, player.userThumbnail),
        preferredPosition: player.playerPreferences?.preferredPosition ?? null,
        preferredLevel: player.playerPreferences?.preferredLevel ?? null
      })) ?? [];

    return {
      isLineupsSaved: false,
      isHost: game.isCurrentUserHost,
      players
    };
  }
}
